import Button from 'ext-ui/components/Button/Button'
import ButtonGroup from 'ext-ui/components/ButtonGroup/ButtonGroup'
import Modal from 'ext-ui/components/Modal/Modal'
import Pill from 'ext-ui/components/Pill/Pill'
import SnowflakeIcon from 'ext-ui/svg/icon/SnowflakeIcon'
import React, { ReactElement, useState } from 'react'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'

import SettingsTile from '../SettingsTile/SettingsTile'
import style, { modalBodyStyle } from './CardStatusLifecycleTile.style'

type CardStatusLifecycleTileProps = {
  creditCard: CreditCardListModel
  /**
   * `DESTROY` on the card. The design restricts Cancel card to Admin/Owner —
   * every other lifecycle row is offered to Card Managers too.
   */
  canCancelCard: boolean
}

type LifecycleRowProps = {
  title: string
  description: string
  ctaLabel: string
  confirmTitle: string
  confirmBody: string
  confirmCtaLabel: string
  /**
   * Defaults to "Cancel". Override where "Cancel" would read as the action
   * rather than the dismissal — the Cancel card row would otherwise offer
   * "Cancel" next to "Cancel card" on the tile's one irreversible action.
   */
  dismissLabel?: string
  severe?: boolean
  testId: string
  onConfirm: () => void
}

function LifecycleRow(props: LifecycleRowProps): ReactElement {
  const {
    title,
    description,
    ctaLabel,
    confirmTitle,
    confirmBody,
    confirmCtaLabel,
    dismissLabel,
    severe = false,
    testId,
    onConfirm,
  } = props
  const translate = useTranslate()
  const [open, setOpen] = useState(false)

  // These rows are the stubs EX-40353 permits, but a stub still must not let a
  // user leave believing their card was blocked. Every confirm body carries the
  // preview disclaimer until the block-code flow is wired.
  const previewNote = translate(
    'This is a preview — nothing is sent and the card is not changed.',
  )

  return (
    <div className="CardStatusLifecycleTile-row">
      <div className="CardStatusLifecycleTile-rowText">
        <div className="CardStatusLifecycleTile-rowTitle">{title}</div>
        <div className="CardStatusLifecycleTile-rowSub">{description}</div>
      </div>
      <Button
        variant="secondary"
        dropShadow={false}
        onClick={() => setOpen(true)}
        data-testid={`${testId}-cta`}
      >
        {ctaLabel}
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={confirmTitle}
        footer={
          <ButtonGroup>
            <Button
              variant="no-style"
              onClick={() => setOpen(false)}
              data-testid={`${testId}-dismiss`}
            >
              {dismissLabel ?? translate('Cancel')}
            </Button>
            <Button
              variant={severe ? 'severe' : 'primary'}
              dropShadow={false}
              onClick={() => {
                onConfirm()
                setOpen(false)
              }}
              data-testid={`${testId}-confirm`}
            >
              {confirmCtaLabel}
            </Button>
          </ButtonGroup>
        }
      >
        <p css={modalBodyStyle}>{confirmBody}</p>
        <p css={modalBodyStyle} data-testid={`${testId}-preview-note`}>
          {previewNote}
        </p>
      </Modal>
    </div>
  )
}

/**
 * "Card status & lifecycle" tile for the redesigned physical card Account
 * Settings page (EX-40354). The status pill reflects the real card state; the
 * lifecycle actions are previews. Freeze is disabled outright rather than
 * flipping local state — there is no block-code API in this client
 * (`CreditCardStatus` has no `Frozen`), so an operable control would tell the
 * user authorizations were blocked when nothing happened.
 */
export default function CardStatusLifecycleTile(
  props: CardStatusLifecycleTileProps,
): ReactElement {
  const { creditCard, canCancelCard } = props
  const translate = useTranslate()

  const isActive = creditCard.isActive

  return (
    <SettingsTile title={translate('Card status & lifecycle')}>
      <div className="CardStatusLifecycleTile" css={style}>
        <div className="CardStatusLifecycleTile-freezeRow">
          <div className="CardStatusLifecycleTile-rowText">
            <div className="CardStatusLifecycleTile-freezeHeading">
              <span className="CardStatusLifecycleTile-rowTitle">
                {translate('Freeze')}
              </span>
              <Pill size="small" color={isActive ? 'highlight' : 'pending'}>
                {isActive ? translate('Active') : translate('Inactive')}
              </Pill>
            </div>
            <div className="CardStatusLifecycleTile-rowSub">
              {translate(
                'Freezing will block new authorizations while credits, refunds, disputes, and statements keep processing. This control is not available yet.',
              )}
            </div>
          </div>
          <Button
            variant="secondary"
            dropShadow={false}
            iconLeft={<SnowflakeIcon />}
            // No `disabledTooltip`: pairing it with `disabled` forces ext-ui
            // Button onto the legacy fallback channel, which would render this
            // differently from the Report/Replace buttons beside it. The row
            // description carries the explanation instead.
            disabled
            data-testid="freeze-cta"
          >
            {translate('Freeze')}
          </Button>
        </div>

        <LifecycleRow
          testId="report-lost"
          title={translate('Report lost or stolen')}
          description={translate(
            'Block the card and ship a replacement with a new number.',
          )}
          ctaLabel={translate('Report')}
          confirmTitle={translate('Report this card lost or stolen?')}
          confirmBody={translate(
            'Once connected, the card will be blocked immediately and a replacement will ship with a new number. Its virtual cards are reissued.',
          )}
          confirmCtaLabel={translate('Report & replace')}
          onConfirm={() => undefined}
        />
        <LifecycleRow
          testId="report-fraud"
          title={translate('Report fraud')}
          description={translate(
            "Flag fraudulent activity for the issuer's fraud team to review.",
          )}
          ctaLabel={translate('Report')}
          confirmTitle={translate('Report fraud on this card?')}
          confirmBody={translate(
            "Once connected, flagged activity will go to the issuer's fraud team for review. The card keeps working unless you also freeze it.",
          )}
          confirmCtaLabel={translate('Report fraud')}
          onConfirm={() => undefined}
        />
        <LifecycleRow
          testId="replace-card"
          title={translate('Replace card')}
          description={translate(
            'Order a new physical card and keep the same number.',
          )}
          ctaLabel={translate('Replace')}
          confirmTitle={translate('Replace this physical card?')}
          confirmBody={translate(
            'Once connected, a new physical card will ship to the billing address. The number stays the same; the current card works until the new one is activated.',
          )}
          confirmCtaLabel={translate('Order replacement')}
          onConfirm={() => undefined}
        />
        {canCancelCard && (
          <LifecycleRow
            testId="cancel-card"
            severe
            title={translate('Cancel card')}
            description={translate(
              'Permanently close this physical card and its virtual cards.',
            )}
            ctaLabel={translate('Cancel card')}
            confirmTitle={translate('Cancel this physical card?')}
            confirmBody={translate(
              "Once connected, this will permanently close the card and all of its virtual cards. This can't be undone.",
            )}
            confirmCtaLabel={translate('Cancel card')}
            dismissLabel={translate('Keep card')}
            onConfirm={() => undefined}
          />
        )}
      </div>
    </SettingsTile>
  )
}
